import { useState, useEffect } from "react";
import { Save } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import {
  Form,
  FormField,
  FormInput,
  FormCheckbox,
  FormActions,
} from "../ui/Form";
import { useLanguage } from "../../contexts/LanguageContext";

const initialFormData = {
  id: null,
  code: "",
  name: "",
  prefix: "",
  addr1: "",
  addr2: "",
  addr3: "",
  addr4: "",
  addr5: "",
  postalCode: "",
  contactName: "",
  contactAdmin: "",
  email: "",
  taxId: "",
  isActive: true,
  isDeleted: false,
  createdAt: "",
  createdBy: ""
};

const DistributorModal = ({
  isOpen,
  onClose,
  onSave,
  distributor = null,
  isLoading = false,
  edit = true,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      if (distributor) {
        setFormData({ ...initialFormData, ...distributor });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [isOpen, distributor]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code?.trim()) {
      newErrors.code = t("distributor.codeRequired") || "Code is required";
    }

    if (!formData.name?.trim()) {
      newErrors.name = t("distributor.nameRequired") || "Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdrop={false}
      title={
        distributor
          ? edit
            ? t("distributor.edit") || "Edit Distributor"
            : formData.code
          : t("distributor.add") || "Add Distributor"
      }
      size="lg"
    >
      <div className="p-6">
        <Form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label={t("distributor.code") || "Code"}
              required
              error={errors.code}
              className="col-span-1"
            >
              <FormInput
                value={formData.code ?? ""}
                onChange={(e) => handleInputChange("code", e.target.value)}
                placeholder={edit ? t("distributor.enterCode") : ""}
                error={errors.code}
                disabled={!edit}
              />
            </FormField>

            <FormField
              label={t("distributor.prefix") || "Prefix"}
              className="col-span-1"
            >
              <FormInput
                value={formData.prefix ?? ""}
                onChange={(e) => handleInputChange("prefix", e.target.value)}
                placeholder={edit ? t("distributor.enterPrefix") : ""}
                disabled={!edit}
              />
            </FormField>

            <FormField
              label={t("distributor.name") || "Name"}
              required
              error={errors.name}
              className="col-span-1 md:col-span-3"
            >
              <FormInput
                value={formData.name ?? ""}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder={edit ? t("distributor.enterName") : ""}
                error={errors.name}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.contactName") || "Contact Name"} className="col-span-2">
              <FormInput
                value={formData.contactName ?? ""}
                onChange={(e) => handleInputChange("contactName", e.target.value)}
                placeholder={edit ? t("distributor.enterContactName") : ""}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.contactAdmin") || "Contact Admin"} className="col-span-1">
              <FormInput
                value={formData.contactAdmin ?? ""}
                onChange={(e) => handleInputChange("contactAdmin", e.target.value)}
                placeholder={edit ? t("distributor.enterContactAdmin") : ""}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.email") || "Email"} className="col-span-1 md:col-span-2">
              <FormInput
                type="email"
                value={formData.email ?? ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder={edit ? t("distributor.enterEmail") : ""}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.taxId") || "Tax ID"} className="col-span-1">
              <FormInput
                value={formData.taxId ?? ""}
                onChange={(e) => handleInputChange("taxId", e.target.value)}
                placeholder={edit ? t("distributor.enterTaxId") : ""}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.addressLine1") || "Address Line 1"} className="col-span-3">
              <FormInput
                value={formData.addr1 ?? ""}
                onChange={(e) => handleInputChange("addr1", e.target.value)}
                placeholder={edit ? t("distributor.enterAddress") : ""}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.addressLine2") || "Address Line 2"} className="col-span-3">
              <FormInput
                value={formData.addr2 ?? ""}
                onChange={(e) => handleInputChange("addr2", e.target.value)}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.addressLine3") || "Address Line 3"} className="col-span-1">
              <FormInput
                value={formData.addr3 ?? ""}
                onChange={(e) => handleInputChange("addr3", e.target.value)}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.addressLine4") || "Address Line 4"} className="col-span-1">
              <FormInput
                value={formData.addr4 ?? ""}
                onChange={(e) => handleInputChange("addr4", e.target.value)}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.addressLine5") || "Address Line 5"} className="col-span-1">
              <FormInput
                value={formData.addr5 ?? ""}
                onChange={(e) => handleInputChange("addr5", e.target.value)}
                disabled={!edit}
              />
            </FormField>

            <FormField label={t("distributor.postalCode") || "Postal Code"} className="col-span-1">
              <FormInput
                value={formData.postalCode ?? ""}
                onChange={(e) => handleInputChange("postalCode", e.target.value)}
                placeholder={edit ? t("distributor.enterPostalCode") : ""}
                disabled={!edit}
              />
            </FormField>

            {edit && (
              <div className="col-span-1 md:col-span-3">
                <FormCheckbox
                  label={t("distributor.active") || "Active"}
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange("isActive", e.target.checked)}
                />
              </div>
            )}
          </div>

          <FormActions>
            {edit ? (
              <>
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  {t("common.cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? t("common.saving") : t("common.save")}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={onClose}>
                {t("common.close")}
              </Button>
            )}
          </FormActions>
        </Form>
      </div>
    </Modal>
  );
};

export default DistributorModal;